import type {ReactElement} from 'react';
import {useRef} from 'react';
import React, {useEffect} from 'react';
import {Text, TextSize} from '@Components/text';
import {InputField} from '@Components/input-field-v2';
import {InputFieldSize} from '@Components/input-field-v2/input-field.types';
import {useDispatch} from 'react-redux';
import {
    useEventWizardDescription,
    useEventWizardEndDateTime, useEventWizardIsAIDescriptionGenerating,
    useEventWizardOfflineVenue,
    useEventWizardSelectedType,
    useEventWizardShouldCheckErrState,
    useEventWizardStartDateTime,
    useEventWizardTitle,
} from '@Components/event-wizard/event-wizard.hooks';
import {onEventWizardDescriptionChange, onEventWizardIsAIDescriptionGeneratingUpdate, onEventWizardIsDescriptionUserMadeUpdate, onEventWizardShouldNextBtnBeDisabled} from '@Components/event-wizard/event-wizard-slice';
import type {AiGeneratedDescriptionParams} from '@Components/event-wizard/event-wizard-description/event-wizard-description.types';
import {MAX_EVENT_DESCRIPTION_LENGTH} from '@Components/event-wizard/event-wizard-description/event-wizard-description.types';
import {updatePreviewDescription} from '@Components/event-wizard/preview/preview.helper';
import {
    getDateForAIGenerate,
    getTimeForAIGenerate,
    shouldUpsellUserDescriptionAIGeneration,
} from '@Components/event-wizard/event-wizard-description/event-wizard-description.helper';
import {AiButton} from '@Components/ai-button';
import {Size} from '@Components/button';
import {AIButtonVariant} from '@Components/ai-button/ai-button.types';
import {EventWizardErrorInfo} from '@Components/event-wizard/event-wizard-error-info';
import {isDescriptionSectionComplete} from '@Components/event-wizard/event-wizard-review/event-wizard-review.helper';
import {EventWizardDescriptionMarkdown} from '@Components/event-wizard/event-wizard-description-markdown';
import styles from './event-wizard-description.module.scss';
import {openEventsUpsellDialog, EventUpsellType} from '@Libraries/events.library';
import {getPlainTextFromMarkdown, removeLineSpaces} from '@Components/event-wizard/event-wizard-description-markdown/event-wizard-description-markdown.types';
import {hasAjaxRequestBeenMade, recordAjaxRequest, removeRecordedAjaxRequest} from "@Utils/ajax.util";

export function EventWizardDescription(): ReactElement<any> {
    const dispatch = useDispatch();

    const startDateTime = useEventWizardStartDateTime();
    const endDateTime = useEventWizardEndDateTime();

    const type = useEventWizardSelectedType();
    const name = useEventWizardTitle();
    const venue = useEventWizardOfflineVenue()?.name;
    const date = getDateForAIGenerate(startDateTime, endDateTime);
    const timing = getTimeForAIGenerate(startDateTime, endDateTime);
    const description = useEventWizardDescription();
    const shouldCheckErrState = useEventWizardShouldCheckErrState();
    const isAIDescriptionGenerating = useEventWizardIsAIDescriptionGenerating();
    const latestAIGeneratedDescription = useRef('');
    const hasGeneratedAIDesc = useRef(false);

    useEffect(() => {
        if (!description || description === '') {
            rewriteWithAi();
        }
    }, [isAIDescriptionGenerating]);

    const updateDescription = (desc: string): void => {
        if (getPlainTextFromMarkdown(desc).length > MAX_EVENT_DESCRIPTION_LENGTH) {
          return;
        }
        dispatch(onEventWizardDescriptionChange(desc));
        updatePreviewDescription(desc);
    };

    const updatedDescFromAI = (): void => {
        dispatch(onEventWizardIsDescriptionUserMadeUpdate(false));
    };

    const updatedDescFromUser = (desc: string): void => {
        if (getPlainTextFromMarkdown(desc) === removeLineSpaces(latestAIGeneratedDescription.current)) {
            dispatch(onEventWizardIsDescriptionUserMadeUpdate(false));
        } else {
            dispatch(onEventWizardIsDescriptionUserMadeUpdate(true));
        }
    };

    const getDataForAiGeneratedDescription = (): AiGeneratedDescriptionParams => {
        return {
            type: type?.name ?? '',
            name,
            venue,
            date,
            timing,
            description,
        };
    };

    const rewriteWithAi = (isFromBtnClick = false): void => {
        if (hasGeneratedAIDesc.current || isAIDescriptionGenerating || hasAjaxRequestBeenMade('getAiGeneratedEventDescription')) {
            return;
        }

        if (shouldUpsellUserDescriptionAIGeneration()) {
            if (isFromBtnClick) {
                openEventsUpsellDialog(EventUpsellType.DESCRIPTION_REWRITE_AI, undefined);
            }
            return;
        }
        dispatch(onEventWizardShouldNextBtnBeDisabled(true));
        dispatch(onEventWizardIsAIDescriptionGeneratingUpdate(true));
        updateDescription('');
        recordAjaxRequest('getAiGeneratedEventDescription');
        void window.PMW.writeLocal('event/getAiGeneratedEventDescription', getDataForAiGeneratedDescription()).then((aiWrittenDesc: string) => {
            hasGeneratedAIDesc.current = true;
            latestAIGeneratedDescription.current = aiWrittenDesc;
            updateDescription(aiWrittenDesc);
            updatedDescFromAI();
            dispatch(onEventWizardIsAIDescriptionGeneratingUpdate(false));
            dispatch(onEventWizardShouldNextBtnBeDisabled(false));
            removeRecordedAjaxRequest('getAiGeneratedEventDescription');
        });
    };

    const isInErrState = (): boolean => {
        return shouldCheckErrState && !isDescriptionSectionComplete(description);
    };

    return (
        <div>
            <Text val={window.i18next.t('pmwjs_description')} size={TextSize.XXSMALL} bold/>
            {isAIDescriptionGenerating ? (
                <div className={`${styles.aiGeneratingContainer} radius-8 spacing-m-t-1 spacing-m-b-3`}>
                    <div className="bg-neutral-0 radius-8">
                        <InputField
                            input={isAIDescriptionGenerating ? window.i18next.t('pmwjs_aiming_for_ideal_description') : description}
                            inputClassName={`${styles.descriptionInput} ${isAIDescriptionGenerating ? styles.aiGenerating : ''} border-s-dark radius-8`}
                            size={InputFieldSize.XSMALL}
                            maxNumberOfRows={6}
                            isMultiline
                        />
                    </div>
                </div>
            ) : (
                <>
                    <EventWizardDescriptionMarkdown
                        description={description}
                        onUpdate={(desc: string) => {
                            updateDescription(desc);
                            updatedDescFromUser(desc);
                        }}
                        isErrorState={isInErrState()}
                    />
                    {isInErrState() ? <EventWizardErrorInfo text={window.i18next.t('pmwjs_add_description_proceed')}/> : undefined}
                </>
            )}

            <div className="flex-row-align-center justify-content-end _full-width">
                <AiButton
                    onClick={() => {
                        hasGeneratedAIDesc.current = false;
                        rewriteWithAi(true);
                    }}
                    customClasses="radius-8"
                    variant={AIButtonVariant.SUBDUED}
                    size={Size.XSMALL}
                    text={shouldUpsellUserDescriptionAIGeneration() ? window.i18next.t('pmwjs_write_with_ai') : window.i18next.t('pmwjs_rewrite_with_ai')}
                    icon="icon-ai-caption"
                    isLoading={isAIDescriptionGenerating}
                    showPremiumUpsellIcon={shouldUpsellUserDescriptionAIGeneration()}
                />
            </div>
        </div>
    );
}
